import Vue from 'vue'
import Router from 'vue-router'
import RouterPrefetch from 'vue-router-prefetch'
import store from '../store'
import * as R from 'ramda'
import guards from './nav-guards'
import * as MR from '@/libs/maybe-ramda'
import i18n from '@/i18n'

const asyncImport = (dir) => (view) => () =>
  // https://github.com/babel/babel/issues/10904
  process.env.NODE_ENV === 'development'
    ? import(
      /* webpackChunkName: "[request]" */ '@/' + dir + '/' + view + '.vue'
    )
    : import('@/' + dir + '/' + view + '.vue')

const importView = asyncImport('views')
const importComp = asyncImport('components')

const RgToolbar = importComp('Toolbar')
const RgToolbarInSearch = importComp('ToolbarInSearch')
const RgToolbarDeep = importComp('ToolbarDeep')

const ReviewToolbar = asyncImport('modules')('review/ReviewToolbar')
const ReviewEditToolbar = asyncImport('modules')('review/ReviewEditToolbar')

Vue.use(Router)
Vue.use(RouterPrefetch)

const routes = [
  {
    path: '/rg-admin',
    name: 'admin',
    components: {
      admin: importView('Admin')
    }
  },
  {
    path: '/',
    name: 'home',
    components: {
      default: importView('Home'),
      toolbar: RgToolbar
    },
    meta: { noRootPadding: true, noContentWidth: true },
    beforeEnter (to, from, next) {
      if (R.isNil(from.name) && from.path === '/' && store.getters.user) {
        next({ name: 'library' })
      } else {
        next()
      }
    }
  },
  {
    path: '/signup',
    name: 'signup',
    components: {
      default: importView('Signup'),
      toolbar: RgToolbarDeep
    },
    props: {
      toolbar: { title: i18n.t('routes.signup') }
    },
    beforeEnter: guards.restrictForUser
  },
  {
    path: '/signin',
    name: 'signin',
    components: {
      default: importView('Signin'),
      toolbar: RgToolbarDeep
    },
    props: {
      toolbar: { title: i18n.t('routes.signin') }
    },
    beforeEnter: guards.restrictForUser
  },
  {
    path: '/books',
    name: 'books',
    components: {
      default: importView('Books'),
      toolbar: RgToolbarDeep
    },
    props: {
      toolbar: { title: i18n.t('routes.searchBook') }
    }
  },
  {
    path: '/books/:provider/:isbn',
    name: 'bookDetail',
    components: {
      default: importView('BookDetail'),
      toolbar: RgToolbarDeep
    },
    props: {
      default (route) {
        return { data: route.params.data }
      }
    }
  },
  {
    path: '/library',
    name: 'library',
    components: {
      default: importView('MyLibrary'),
      toolbar: RgToolbar
    },
    beforeEnter: guards.requireUserInfo,
    meta: { noRootPadding: true },
    props: {
      default (route) {
        const libraryFirstView = MR.of(store.getters.settings)
          .chain(MR.prop('libraryFirstView'))
          .getOrElse('book')

        return {
          view: route.query.view || libraryFirstView
        }
      },
      toolbar: { title: i18n.t('routes.myLibrary') }
    }
  },
  {
    path: '/library/books/:bookId',
    name: 'myBook',
    components: {
      default: importView('MyBook'),
      toolbar: importComp('toolbars/MyBookToolbar')
    },
    beforeEnter: guards.requireUserInfo,
    props: { default: true }
  },
  {
    path: '/library/books/:bookId/toc',
    name: 'toc',
    components: {
      default: importView('TableOfContents'),
      toolbar: RgToolbarDeep
    },
    props: {
      toolbar: { title: i18n.t('routes.toc') }
    }
  },
  {
    path: '/library/books/:bookId/review',
    name: 'review',
    components: {
      default: importView('Review'),
      toolbar: ReviewToolbar
    },
    meta: {
      noContentWidth: true,
      noRootPadding: true
    },
    props: {
      toolbar: { title: i18n.t('routes.review') }
    }
  },
  {
    path: '/library/books/:bookId/review/edit',
    name: 'reviewEdit',
    components: {
      default: importView('ReviewEdit'),
      toolbar: ReviewEditToolbar
    },
    meta: {
      noRootPadding: true
    },
    props: {
      toolbar: { title: i18n.t('routes.reviewEdit') }
    }
  },
  {
    path: '/forgot',
    name: 'forgot',
    components: {
      default: importView('Forgot'),
      toolbar: RgToolbarDeep
    },
    props: {
      toolbar: { title: i18n.t('routes.forgot') }
    }
  },
  {
    path: '/policies/privacy',
    name: 'privacy',
    components: {
      default: importView('Privacy'),
      toolbar: RgToolbarDeep
    },
    props: {
      toolbar: { title: i18n.t('routes.privacy') }
    }
  },
  {
    path: '/policies/terms-of-service',
    name: 'termsOfService',
    components: {
      default: importView('TermsOfService'),
      toolbar: RgToolbarDeep
    },
    props: {
      toolbar: { title: i18n.t('routes.tos') }
    }
  },
  {
    path: '/settings',
    name: 'settings',
    components: {
      default: importView('Settings'),
      toolbar: RgToolbarDeep
    },
    meta: {
      noContentWidth: true,
      noRootPadding: true
    },
    props: {
      toolbar: { title: i18n.t('routes.settings') }
    },
    beforeEnter: guards.requireUserInfo
  },
  {
    path: '/tag-manager',
    name: 'tagManager',
    components: {
      default: importView('Tags'),
      toolbar: RgToolbarDeep
    },
    props: {
      toolbar: { title: i18n.t('routes.tags') }
    },
    beforeEnter: guards.requireAuth
  },
  {
    path: '/search',
    name: 'searched',
    components: {
      default: importView('Searched'),
      toolbar: RgToolbarInSearch
    },
    props: {
      default: (route) => route.query,
      toolbar: (route) => route.query
    }
  },
  {
    path: '/plans',
    name: 'plans',
    components: {
      default: importView('Plans'),
      toolbar: RgToolbarDeep
    },
    meta: {
      noContentWidth: true,
      noRootPadding: true
    },
    props: {
      toolbar: { title: i18n.t('routes.pricing') }
    },
    beforeEnter: guards.requireOfferings
  },
  {
    path: '/import-services',
    name: 'import-services',
    components: {
      default: importView('ImportServices'),
      toolbar: RgToolbarDeep
    },
    props: {
      toolbar: { title: i18n.t('routes.services') }
    }
  },
  {
    path: '/import-services/:name/register',
    name: 'service-register',
    components: {
      default: importView('ServiceRegister'),
      toolbar: RgToolbarDeep
    },
    props: {
      default: (route) => ({ provider: route.params.name }),
      toolbar: (route) => {
        const names = {
          ridi: '리디북스',
          millie: '밀리의서재'
        }

        return {
          title: i18n.t('routes.serviceRegister', {
            service: names[route.params.name]
          })
        }
      }
    }
  },
  {
    path: '/delete-account',
    name: 'delete-account',
    components: {
      default: importView('AccountDeletion'),
      toolbar: RgToolbarDeep
    },
    props: {
      toolbar: {
        title: i18n.t('routes.deleteAccount')
      }
    },
    beforeEnter: guards.requireAuth
  },
  {
    path: '/charts',
    name: 'charts',
    components: {
      default: importView('Charts'),
      toolbar: RgToolbarDeep
    },
    props: {
      toolbar: {
        title: i18n.t('routes.charts')
      }
    },
    beforeEnter: guards.requireAuth
  },
  {
    path: '/notes/add-to-list',
    name: 'add-to-list',
    components: {
      default: importView('AddToList'),
      toolbar: RgToolbarDeep
    },
    props: {
      default (route) {
        return { memos: route.params.memos }
      },
      toolbar: {
        title: i18n.t('routes.addToList')
      }
    },
    beforeEnter: guards.requireAuth
  },
  {
    path: '/notelists',
    name: 'notelists',
    components: {
      default: importView('Notelists'),
      toolbar: importComp('toolbars/Notelists')
    },
    props: {
      toolbar: {
        title: i18n.t('routes.notelists')
      }
    },
    beforeEnter: guards.requireAuth
  },
  {
    path: '/notelists/:listId',
    name: 'notelist',
    components: {
      default: importView('Notelist'),
      toolbar: importComp('toolbars/Notelist')
    },
    beforeEnter: guards.requireAuth
  },
  {
    path: '/s/notelists/:listId',
    name: 'view-notelist',
    components: {
      default: importView('ViewNotelist'),
      toolbar: importComp('toolbars/ViewNotelist')
    }
  }
]

const router = new Router({ routes })

router.beforeEach(guards.cleanUpView)
router.afterEach(guards.cachePreRoute)

export default router
